import { useState, useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { Bell, Menu, ShoppingBag, Package, AlertCircle, X, Wallet } from 'lucide-react';
import { FaSun, FaCloudSun, FaMoon } from 'react-icons/fa';

import { RootState } from '../../store/store';
import api, { sellerAPI, orderAPI } from '../../api';
import Swal from 'sweetalert2';
import axios from 'axios';
import ServicePath from '../../Webservice/Webservice';

interface TopbarProps {
  onMenuClick: () => void;
}

export default function Topbar({ onMenuClick }: TopbarProps) {
  const { seller: authSeller } = useSelector((s: RootState) => s.auth);
  const [seller, setSeller] = useState(authSeller);
  const [walletBalance, setWalletBalance] = useState(0);
  const [notifications, setNotifications] = useState<any[]>([]);
  const [notificationsLoading, setNotificationsLoading] = useState(true);
  const [notificationsLoadingMore, setNotificationsLoadingMore] = useState(false);
  const [notificationsPage, setNotificationsPage] = useState(1);
  const [notificationsHasMore, setNotificationsHasMore] = useState(true);
  const [notificationsTotal, setNotificationsTotal] = useState(0);
  const [unreadTotal, setUnreadTotal] = useState(0);
  const NOTIFICATIONS_PAGE_SIZE = 10;

  const mapNotification = (n: any) => {
    const dt = new Date(n.createdAt);
    const dateStr = dt.toLocaleDateString('en-GB').replace(/\//g, '/');
    const timeStr = dt.toLocaleTimeString('en-US', { hour12: true });
    const formattedTime = `${dateStr}, ${timeStr}`;

    if (n.type === 'product_added') {
      return {
        id: n._id,
        type: 'product',
        title: 'New Product Added',
        message: `Admin added a new product: ${n.productName || 'Unknown Product'}`,
        time: formattedTime,
        icon: Package,
        color: '#3b82f6',
        orderData: { _id: n.productId, orderId: null }, // _id is used for marking read
        isRead: n.isRead === true
      };
    }
    return {
      id: n._id, // This is the OrderNotification document _id
      type: 'order',
      title: 'New Order Received',
      message: `Order #${n.orderId} from ${n.customerName || 'Customer'} - ₹${n.totalAmount || 0}`,
      time: formattedTime,
      icon: ShoppingBag,
      color: '#10b981',
      orderData: { _id: n.orderId, orderId: n.orderId }, // Map to what Topbar expects for navigation
      isRead: n.isRead === true
    };
  };

  const fetchNotifications = (page: number, append: boolean) => {
    if (append) setNotificationsLoadingMore(true);
    else setNotificationsLoading(true);

    orderAPI.getNotifications({ page, limit: NOTIFICATIONS_PAGE_SIZE }).then(res => {
      if (res.data?.data) {
        const mappedNotifications = res.data.data.map(mapNotification);
        setNotifications(prev => append ? [...prev, ...mappedNotifications] : mappedNotifications);
        setNotificationsHasMore(Boolean(res.data.hasMore));
        setNotificationsTotal(res.data.total || 0);
        if (typeof res.data.unreadCount === 'number') setUnreadTotal(res.data.unreadCount);
        setNotificationsPage(page);
      }
    }).catch(err => {
      console.error("Error fetching notifications:", err);
    }).finally(() => {
      setNotificationsLoading(false);
      setNotificationsLoadingMore(false);
    });
  };

  const loadMoreNotifications = () => {
    if (notificationsLoadingMore || notificationsLoading || !notificationsHasMore) return;
    fetchNotifications(notificationsPage + 1, true);
  };

  useEffect(() => {
    sellerAPI.getProfile().then(res => {
      const s = res.data.seller;
      setSeller(s);
    }).catch(err => console.error(err));

    const fetchWallet = () => {
      if (authSeller?._id) {
        api.get(`/seller-wallet/${authSeller._id}`)
          .then(res => {
            if (res.data.success) {
              setWalletBalance(res.data.data.wallet.availableBalance || 0);
            }
          })
          .catch(err => console.error("Error fetching Topbar wallet:", err));
      }
    };

    fetchWallet();

    window.addEventListener('walletUpdated', fetchWallet);

    // Fetch first page of notifications; further pages load lazily on scroll
    fetchNotifications(1, false);

    return () => {
      window.removeEventListener('walletUpdated', fetchWallet);
    };
  }, [authSeller?._id]);

  const navigate = useNavigate();
  const location = useLocation();
  const [showNotifications, setShowNotifications] = useState(false);
  const [showAllNotificationsSideModal, setShowAllNotificationsSideModal] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const handleDropdownScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const bottom = e.currentTarget.scrollHeight - e.currentTarget.scrollTop <= e.currentTarget.clientHeight + 10;
    if (bottom) loadMoreNotifications();
  };

  const handleModalScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const bottom = e.currentTarget.scrollHeight - e.currentTarget.scrollTop <= e.currentTarget.clientHeight + 10;
    if (bottom) loadMoreNotifications();
  };

  useEffect(() => {
    setShowNotifications(false);
    setShowAllNotificationsSideModal(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowNotifications(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleMarkAsRead = async (id: string, orderId: string) => {
    try {
      await orderAPI.markNotificationViewed({ orderId });
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, isRead: true } : n));
      setUnreadTotal(prev => Math.max(prev - 1, 0));
    } catch (error) {
      console.error("Error marking notification as read:", error);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await orderAPI.markAllNotificationsViewed();
      setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
      setUnreadTotal(0);
    } catch (error) {
      console.error("Error marking all notifications as read:", error);
    }
  };

  const unreadCount = unreadTotal;

  const getGreetingData = () => {
    const hrs = new Date().getHours();
    if (hrs < 12) return { text: 'Good morning', icon: <FaCloudSun size={18} color="#f59e0b" style={{ display: 'flex', alignItems: 'center' }} /> };
    if (hrs < 17) return { text: 'Good afternoon', icon: <FaSun size={18} color="#eab308" style={{ display: 'flex', alignItems: 'center' }} /> };
    return { text: 'Good evening', icon: <FaMoon size={16} color="#6366f1" style={{ display: 'flex', alignItems: 'center' }} /> };
  };

  const { text: greetingText, icon: greetingIcon } = getGreetingData();

  const isRestrictedView = seller?.isBlock || seller?.verification_status === 'P' || seller?.verification_status === 'R';

  return (
    <div style={{
      background: 'rgba(255, 255, 255, 0.85)',
      backdropFilter: 'blur(16px)',
      WebkitBackdropFilter: 'blur(16px)',
      borderBottom: '1px solid rgba(241, 245, 249, 0.8)',
      padding: '0 32px',
      height: '70px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: isRestrictedView ? 'flex-end' : 'space-between',
      position: 'sticky',
      top: 0,
      zIndex: 30,
      transition: 'all 0.3s ease',
    }}>
      {/* Left: mobile menu + breadcrumb */}
      {!isRestrictedView && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
          <button
            onClick={onMenuClick}
            style={{
              display: 'none',
              background: 'rgba(241, 245, 249, 0.5)',
              border: 'none',
              cursor: 'pointer',
              padding: '8px',
              borderRadius: '10px',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s ease',
            }}
            className="mobile-menu-btn"
          >
            <Menu size={20} color="#334155" />
          </button>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span className="greeting-text" style={{ fontSize: '15px', color: '#64748b', fontWeight: '500' }}>{greetingText}<span className="topbar-greeting">,</span></span>
              <span className="topbar-greeting greeting-text" style={{ fontSize: '15px', color: '#0f172a', fontWeight: '500' }}>
                {
                  (
                    seller?.storeinfo?.owner_name ||
                    seller?.storeinfo?.store_name ||
                    seller?.contactPerson ||
                    seller?.businessName ||
                    'My Store'
                  ).length > 25
                    ? (
                      seller?.storeinfo?.owner_name ||
                      seller?.storeinfo?.store_name ||
                      seller?.contactPerson ||
                      seller?.businessName ||
                      'My Store'
                    ).slice(0, 25) + '...'
                    : (
                      seller?.storeinfo?.owner_name ||
                      seller?.storeinfo?.store_name ||
                      seller?.contactPerson ||
                      seller?.businessName ||
                      'My Store'
                    )
                }
              </span>
              <span style={{ display: 'flex', alignItems: 'center' }}>{greetingIcon}</span>
            </div>
          </div>
        </div>
      )}

      {/* Right: actions */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', position: 'relative' }}>

        {/* Wallet System */}
        {!isRestrictedView && (
          <div
            onClick={() => navigate('/wallet')}
            style={{
              display: 'flex', alignItems: 'center', gap: '8px',
              background: 'rgba(20, 184, 166, 0.08)',
              border: '1px solid rgba(20, 184, 166, 0.2)',
              padding: '8px 14px', borderRadius: '12px',
              cursor: 'pointer', transition: 'all 0.3s ease',
            }}
            className="topbar-wallet-btn"
            title="My Wallet"
          >
            <Wallet size={18} color="#14b8a6" />
            <span style={{ fontSize: '14px', fontWeight: '700', color: '#0f172a' }}>
              ₹{walletBalance.toLocaleString('en-IN')}
            </span>
          </div>
        )}

        {/* Notifications Dropdown */}
        {!isRestrictedView && (
          <div ref={dropdownRef} style={{ position: 'relative' }}>
            <button
              onClick={() => setShowNotifications(!showNotifications)}
              style={{
                width: '42px', height: '42px', borderRadius: '12px',
                background: showNotifications ? 'rgba(20, 184, 166, 0.08)' : '#f8fafc',
                border: showNotifications ? '1px solid rgba(20, 184, 166, 0.2)' : '1px solid #f1f5f9',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer', position: 'relative', transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                boxShadow: showNotifications ? '0 4px 12px rgba(20, 184, 166, 0.08)' : 'none',
              }}
              className="topbar-btn"
              title="Notifications"
            >
              <Bell size={18} color={showNotifications ? '#14b8a6' : '#64748b'} style={{ transition: 'color 0.2s ease' }} />
              {/* Active notification indicator with count */}
              {unreadCount > 0 && <span style={{
                position: 'absolute', top: '2px', right: '2px',
                background: '#ef4444', color: 'white',
                borderRadius: '20px', padding: '1px 5px',
                fontSize: '9px', fontWeight: '800',
                border: '2px solid white',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: '0 2px 5px rgba(239, 68, 68, 0.4)',
                minWidth: '16px', height: '16px',
                lineHeight: 1
              }}>
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>}
            </button>

            {showNotifications && (
              <div className="glass-card shadow-lg" style={{
                position: 'absolute', top: '54px', right: '0', width: '340px',
                background: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(20px)',
                WebkitBackdropFilter: 'blur(20px)',
                borderRadius: '16px', border: '1px solid rgba(241, 245, 249, 0.8)',
                boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)',
                overflow: 'hidden', animation: 'scale-up 0.2s cubic-bezier(0.34, 1.56, 0.64, 1) forwards',
                zIndex: 100,
              }}>
                <div style={{ padding: '14px 18px', borderBottom: '1px solid rgba(241, 245, 249, 0.8)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <h4 style={{ margin: 0, fontWeight: '800', fontSize: '14px', color: '#0f172a', fontFamily: '"Poppins", sans-serif' }}>
                    Notifications{notificationsTotal > 0 && <span style={{ color: '#94a3b8', fontWeight: '600' }}> ({notificationsTotal})</span>}
                  </h4>
                  {unreadCount > 0 && (
                    <span onClick={handleMarkAllRead} style={{ fontSize: '11px', color: '#14b8a6', fontWeight: '700', cursor: 'pointer', transition: 'opacity 0.2s' }} className="hover-opacity-80">Mark all read</span>
                  )}
                </div>
                <div style={{ maxHeight: '360px', overflowY: 'auto' }} className="custom-scrollbar" onScroll={handleDropdownScroll}>
                  {notificationsLoading ? (
                    <div style={{ padding: '40px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                      <div style={{ width: '30px', height: '30px', border: '3px dotted #14b8a6', borderRadius: '50%', animation: 'spin 2s linear infinite' }}></div>
                    </div>
                  ) : notifications.length === 0 ? (
                    <div style={{ padding: '20px', textAlign: 'center', color: '#64748b', fontSize: '13px' }}>
                      No recent notifications
                    </div>
                  ) : <>
                    {notifications.map((n) => {
                    const Icon = n.icon;
                    return (
                      <div key={n.id} onClick={() => {
                        if (!n.isRead && n.orderData?._id) {
                          handleMarkAsRead(n.id, n.orderData._id);
                        }
                        setShowNotifications(false);
                        if (n.type === 'product') {
                          navigate('/products/add');
                        } else {
                          navigate('/orders', { state: { orderId: n.orderData?.orderId } });
                        }
                      }} style={{
                        padding: '14px 18px', display: 'flex', gap: '14px',
                        cursor: 'pointer', borderBottom: '1px solid rgba(248, 250, 252, 0.8)',
                        background: n.isRead ? 'rgba(241, 245, 249, 0.5)' : '#ffffff',
                        transition: 'all 0.2s ease',
                      }} className="notification-item">
                        <div style={{
                          width: '38px', height: '38px', borderRadius: '12px', background: `${n.color}11`,
                          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                        }}>
                          <Icon size={16} color={n.color} />
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: '13px', fontWeight: '700', color: '#1e293b', lineHeight: '1.3', marginBottom: '3px' }}>{n.title}</div>
                          <div style={{ fontSize: '11px', color: '#64748b', lineHeight: '1.4', marginBottom: '6px' }}>{n.message}</div>
                          <div style={{ fontSize: '10px', color: '#94a3b8', fontWeight: '600' }}>{n.time}</div>
                        </div>
                      </div>
                    );
                    })}
                    {notificationsLoadingMore && (
                      <div style={{ padding: '14px', display: 'flex', justifyContent: 'center' }}>
                        <div style={{ width: '20px', height: '20px', border: '2px dotted #14b8a6', borderRadius: '50%', animation: 'spin 2s linear infinite' }}></div>
                      </div>
                    )}
                  </>}
                </div>
                <div
                  onClick={() => { setShowNotifications(false); setShowAllNotificationsSideModal(true); }}
                  style={{
                    padding: '12px',
                    textAlign: 'center',
                    background: 'rgba(248, 250, 252, 0.8)',
                    cursor: 'pointer',
                    fontSize: '12px',
                    fontWeight: '700',
                    color: '#14b8a6',
                    borderTop: '1px solid rgba(241, 245, 249, 0.8)',
                    transition: 'background 0.2s ease'
                  }}
                  className="view-all-btn"
                >
                  View All Activity
                </div>
              </div>
            )}
          </div>
        )}
        {/* Profile Avatar Button */}

        <button
          onClick={() => navigate('/profile')}
          style={{
            width: '42px', height: '42px', borderRadius: '50%', padding: '1.5px',
            background: 'linear-gradient(135deg, #14b8a6, #0d9488)',
            border: 'none', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: 'white', fontWeight: '800', fontSize: '15px',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            boxShadow: '0 4px 12px rgba(20, 184, 166, 0.25)',
            fontFamily: '"Poppins", sans-serif'
          }}
          className="topbar-avatar"
        >
          {seller?.storeLogo ? (
            <img src={seller?.storeLogo} alt="Logo" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
          ) : (
            <span style={{ color: 'white', fontWeight: '800', fontSize: '20px' }}>
              {(seller?.storeinfo?.store_name || seller?.businessName || seller?.contactPerson || 'S')?.[0]?.toUpperCase()}
            </span>
          )}
        </button>

      </div>

      {/* Full-height right side modal for All Activity */}
      {showAllNotificationsSideModal && (
        <>
          {/* Overlay */}
          <div 
            onClick={() => setShowAllNotificationsSideModal(false)}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(15, 23, 42, 0.3)',
              zIndex: 9998,
              backdropFilter: 'blur(2px)',
              animation: 'fade-in 0.2s ease-out'
            }}
          />
          
          {/* Modal Container */}
          <div style={{
            position: 'fixed',
            top: 0,
            right: 0,
            height: '100vh',
            width: '480px', /* Matches typical width of the dashboard right-side card (1/3 of grid) */
            maxWidth: '100vw',
            background: 'rgba(255, 255, 255, 0.98)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            boxShadow: '-8px 0 30px rgba(0,0,0,0.1)',
            zIndex: 9999,
            display: 'flex',
            flexDirection: 'column',
            animation: 'slide-in-right 0.35s cubic-bezier(0.16, 1, 0.3, 1) forwards'
          }}>
            {/* Modal Header */}
            <div style={{ padding: '24px', borderBottom: '1px solid rgba(241, 245, 249, 1)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '800', color: '#0f172a', fontFamily: '"Poppins", sans-serif' }}>
                  All Activity{notificationsTotal > 0 && <span style={{ color: '#94a3b8', fontWeight: '600' }}> ({notificationsTotal})</span>}
                </h3>
                <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#64748b' }}>Your latest notifications and updates</p>
              </div>
              <button 
                onClick={() => setShowAllNotificationsSideModal(false)} 
                style={{ background: '#f1f5f9', border: 'none', cursor: 'pointer', color: '#64748b', width: '36px', height: '36px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s' }} 
                className="hover-bg-e2e8f0"
              >
                <X size={18} />
              </button>
            </div>
            
            {/* Modal Body */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '10px 0' }} className="custom-scrollbar" onScroll={handleModalScroll}>
               {notificationsLoading ? (
                 <div style={{ padding: '60px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                   <div style={{ width: '40px', height: '40px', border: '4px dotted #14b8a6', borderRadius: '50%', animation: 'spin 2s linear infinite' }}></div>
                 </div>
               ) : notifications.length === 0 ? (
                 <div style={{ padding: '60px 20px', textAlign: 'center', color: '#94a3b8' }}>
                   <Bell size={48} style={{ margin: '0 auto 16px', opacity: 0.4 }} />
                   <div style={{ fontSize: '16px', fontWeight: '600', color: '#64748b' }}>No activity found</div>
                   <div style={{ fontSize: '13.5px', marginTop: '6px' }}>You're all caught up!</div>
                 </div>
               ) : <>
                 {notifications.map((n) => {
                 const Icon = n.icon;
                 return (
                   <div key={n.id} onClick={() => {
                     if (!n.isRead && n.orderData?._id) {
                       handleMarkAsRead(n.id, n.orderData._id);
                     }
                     setShowAllNotificationsSideModal(false);
                     if (n.type === 'product') {
                       navigate('/products/add');
                     } else {
                       navigate('/orders', { state: { orderId: n.orderData?.orderId } });
                     }
                   }} style={{
                     padding: '20px 24px', display: 'flex', gap: '16px',
                     cursor: 'pointer', borderBottom: '1px solid rgba(241, 245, 249, 0.6)',
                     background: n.isRead ? 'transparent' : 'rgba(241, 245, 249, 0.4)',
                     transition: 'all 0.2s ease',
                   }} className="notification-item">
                     <div style={{
                       width: '46px', height: '46px', borderRadius: '14px', background: `${n.color}15`,
                       display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                     }}>
                       <Icon size={22} color={n.color} />
                     </div>
                     <div style={{ flex: 1 }}>
                       <div style={{ fontSize: '14.5px', fontWeight: '700', color: '#0f172a', marginBottom: '4px' }}>{n.title}</div>
                       <div style={{ fontSize: '13px', color: '#475569', lineHeight: '1.45', marginBottom: '8px' }}>{n.message}</div>
                       <div style={{ fontSize: '11px', color: '#94a3b8', fontWeight: '600' }}>{n.time}</div>
                     </div>
                     {!n.isRead && (
                       <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#14b8a6', flexShrink: 0, marginTop: '6px' }} />
                     )}
                   </div>
                 );
                 })}
                 {notificationsLoadingMore && (
                   <div style={{ padding: '20px', display: 'flex', justifyContent: 'center' }}>
                     <div style={{ width: '26px', height: '26px', border: '3px dotted #14b8a6', borderRadius: '50%', animation: 'spin 2s linear infinite' }}></div>
                   </div>
                 )}
               </>}
            </div>
          </div>
        </>
      )}

      <style>{`
        .topbar-btn:hover {
          background: #f1f5f9 !important;
          transform: translateY(-1px);
        }
        .topbar-wallet-btn:hover {
          background: rgba(20, 184, 166, 0.15) !important;
          transform: translateY(-1px);
        }
        .topbar-avatar:hover {
          transform: translateY(-1.5px) scale(1.02);
          box-shadow: 0 6px 16px rgba(20, 184, 166, 0.35);
        }
        .notification-item:hover {
          background: rgba(248, 250, 252, 0.85);
        }
        .view-all-btn:hover {
          background: rgba(241, 245, 249, 0.9) !important;
        }
        .hover-bg-e2e8f0:hover {
          background: #e2e8f0 !important;
        }
        @keyframes slide-in-right {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
        @keyframes fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes scale-up {
          from { opacity: 0; transform: scale(0.96) translateY(-4px); }
          to { opacity: 1; transform: scale(1) translateY(0); }
        }
        @media (max-width: 510px) {
          .greeting-text {
            display: none !important;
          }
        }
        .custom-scrollbar::-webkit-scrollbar {
          width: 5px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #94a3b8;
        }
      `}</style>
    </div>
  );
}
